import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent } from "@/lib/authAudit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/Auth";
import { resolveTenantForHost, beginSsoSignIn, type SsoTenant } from "@/lib/sso/tenantResolver";
import csvProLogo from "@/assets/csv-pro-logo-new.png";
import csvProLogoLogin from "@/assets/csv-pro-logo-login.png";
import SEO from "@/components/seo/SEO";
import { motion } from "framer-motion";

export default function LoginPage() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ssoTenant, setSsoTenant] = useState<SsoTenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate("/post-login", { replace: true });
    });
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && session) {
      navigate("/post-login", { replace: true });
    }
  }, [session, authLoading, navigate]);

  // Resolve SSO tenant for the current host. If a tenant exists and is active,
  // we render a "Sign in with Microsoft" button above the password form.
  useEffect(() => {
    let cancelled = false;
    console.log("[LoginPage] window.location.host=", window.location.host);
    resolveTenantForHost(window.location.host).then((t) => {
      console.log("[LoginPage] resolved tenant=", t, " willRenderButton=", !!t);
      if (!cancelled) setSsoTenant(t);
    });
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        logAuthEvent(null, email, "login_failed", { reason: error.message });
        throw error;
      }
      logAuthEvent(signInData.user?.id ?? null, email, "login_success");
      // NOTE: MFA challenge (if any) is handled by MfaEnforcementGate on the
      // first protected route after /post-login — keeping login flow intact
      // for users without MFA factors.
      navigate("/post-login", { replace: true });
    } catch (err: any) {
      setError(err.message ?? "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(180deg, #f5f7fb 0%, #ffffff 40%)" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Sign in | CSV Pro" description="Secure login to the CSV Pro cybersecurity readiness platform." />
      <div className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden bg-background">
        {/* Animated background gradient */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 80% 60% at 50% 40%, hsl(220 40% 94% / 0.7), transparent)",
          }}
          animate={{
            background: [
              "radial-gradient(ellipse 80% 60% at 50% 40%, hsl(220 40% 94% / 0.7), transparent)",
              "radial-gradient(ellipse 70% 70% at 55% 45%, hsl(220 40% 92% / 0.6), transparent)",
              "radial-gradient(ellipse 80% 60% at 45% 38%, hsl(220 40% 95% / 0.7), transparent)",
              "radial-gradient(ellipse 80% 60% at 50% 40%, hsl(220 40% 94% / 0.7), transparent)",
            ],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />

        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col items-center"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Branding — tight grouping */}
          <div className="text-center mb-8">
            <img
              src={csvProLogoLogin}
              alt="CSV Pro"
              className="h-28 w-auto mx-auto mb-4"
            />
            <h1 className="text-[38px] font-bold tracking-tight text-foreground mb-2">
              Sign in to CSV Pro
            </h1>
            <p className="text-[16px] leading-relaxed text-muted-foreground max-w-[540px] mx-auto">
              Cybersecurity Readiness Intelligence for Critical Infrastructure and Investment Portfolios
            </p>
          </div>

          {/* Login Card */}
          <motion.div
            className="w-full max-w-[440px] bg-card border border-border rounded-xl p-8"
            style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.05)" }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1, ease: "easeOut" }}
          >
            {ssoTenant && (
              <div className="mb-5 space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-lg font-medium"
                  onClick={() => beginSsoSignIn(ssoTenant)}
                >
                  Sign in with Microsoft ({ssoTenant.display_name})
                </Button>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex-1 h-px bg-border" />
                  <span>or sign in with email</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-5" aria-label="Login form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                <p className="text-xs text-muted-foreground">
                  Use your work email to sign in. Accounts are provisioned by your administrator.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div role="alert" className="text-destructive text-sm bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <motion.div whileHover={{ y: -1 }} transition={{ duration: 0.15 }}>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-lg font-semibold shadow-sm hover:shadow-md transition-shadow duration-150"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </motion.div>
            </form>

          </motion.div>
        </motion.div>
      </div>
    </>
  );
}
